import { useQuery, UseQueryResult } from '@tanstack/react-query';
import deploymentService from '../../../services/DeploymentService';

const POLLING_INTERVAL_MS = 60_000;
const DEFAULT_WINDOW_MS = 24 * 60 * 60 * 1000; // 24h

export interface RecentFailuresSummary {
  /** 기준 시각 (ISO). 지난 windowMs 이내 실패 집계. */
  since: string;
  /** 기간 내 `status === 'failed'` 배포 건수. */
  count: number;
  /** 최신 실패 시각 (ISO). 없으면 null. */
  lastFailedAt: string | null;
}

export const recentFailuresQueryKey = (projectId: string | null, windowMs: number) =>
  ['dashboard', 'recent-failures', projectId, windowMs] as const;

interface UseRecentFailuresOptions {
  enabled: boolean;
  windowMs?: number;
}

/**
 * 대시보드 KPI 위젯용 — 최근 `windowMs`(기본 24h) 이내 실패 건수 집계.
 *
 * 현재는 `deploymentService.getDeployments(projectId)` 전체 목록에서
 * frontend 필터링한다. 추후 backend 에 `/deployments?since=...&status=failed`
 * 엔드포인트가 생기면 교체.
 */
export function useRecentFailures(
  projectId: string | null,
  options: UseRecentFailuresOptions,
): UseQueryResult<RecentFailuresSummary, Error> {
  const { enabled, windowMs = DEFAULT_WINDOW_MS } = options;

  return useQuery<RecentFailuresSummary, Error>({
    queryKey: recentFailuresQueryKey(projectId, windowMs),
    queryFn: async () => {
      const now = Date.now();
      const threshold = now - windowMs;
      const since = new Date(threshold).toISOString();
      const res = await deploymentService.getDeployments(projectId as string);
      const failed = (res?.data ?? []).filter((d) => {
        if (d.status !== 'failed') return false;
        const t = new Date(d.deployed_at).getTime();
        return Number.isFinite(t) && t >= threshold;
      });
      failed.sort((a, b) => {
        return new Date(b.deployed_at).getTime() - new Date(a.deployed_at).getTime();
      });
      return {
        since,
        count: failed.length,
        lastFailedAt: failed[0]?.deployed_at ?? null,
      };
    },
    enabled: enabled && !!projectId,
    refetchInterval: () => {
      if (typeof document !== 'undefined' && document.hidden) return false;
      return POLLING_INTERVAL_MS;
    },
    refetchIntervalInBackground: false,
    retry: 2,
    staleTime: 30_000,
  });
}
